import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';
import { supabase } from "@/integrations/supabase/client";

export type MoodType = 'Easy' | 'Meh' | 'Struggled';

export interface MoodEntry {
  id: string;
  date: string;
  mood: MoodType;
  created_at: string;
  updated_at: string;
}

type MoodTrackerContextType = {
  moodEntries: MoodEntry[];
  loading: boolean;
  setMoodForDate: (date: string, mood: MoodType) => Promise<void>;
  getMoodForDate: (date: string) => MoodType | null;
  deleteMoodForDate: (date: string) => Promise<void>;
};

const MoodTrackerContext = createContext<MoodTrackerContextType | undefined>(undefined);

export function useMoodTracker() {
  const context = useContext(MoodTrackerContext);
  if (context === undefined) {
    throw new Error('useMoodTracker must be used within a MoodTrackerProvider');
  }
  return context;
}

// Mood colors - using existing app colors
export const moodColors = {
  Easy: {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(142, 71%, 45%)', // Green
    hover: 'hsl(142, 71%, 55%)'
  },
  Meh: {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(24, 95%, 65%)', // Orange
    hover: 'hsl(24, 95%, 75%)'
  },
  Struggled: {
    empty: 'hsl(var(--habit-empty))',
    filled: 'hsl(0, 90%, 65%)', // Red
    hover: 'hsl(0, 90%, 75%)'
  }
};

export function MoodTrackerProvider({ children }: { children: ReactNode }) {
  const [moodEntries, setMoodEntries] = useState<MoodEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  // Load mood entries on mount and when user changes
  useEffect(() => {
    if (user) {
      loadMoodEntries();
    } else {
      setMoodEntries([]);
      setLoading(false);
    }
  }, [user]);

  const loadMoodEntries = async () => {
    if (!user) return;
    
    try {
      console.log('Loading mood entries from Supabase for user:', user.id);
      
      const { data, error } = await supabase
        .from('mood_tracker')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false });

      if (error) {
        console.error('Error loading mood entries:', error);
        throw error;
      }

      console.log('Loaded mood entries from Supabase:', data);
      
      // Convert database format to our MoodEntry format
      const entries: MoodEntry[] = data.map(row => ({
        id: row.id,
        date: row.date,
        mood: row.mood as MoodType,
        created_at: row.created_at || new Date().toISOString(),
        updated_at: row.updated_at || new Date().toISOString()
      }));
      
      setMoodEntries(entries);

      // Check for localStorage data to migrate (only if no Supabase data exists)
      if (entries.length === 0) {
        await migrateLocalStorageData();
      }
      
    } catch (error) {
      console.error("Error loading mood entries:", error);
      toast.error("Failed to load mood data");
      setMoodEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const migrateLocalStorageData = async () => {
    if (!user) return;

    try {
      const storageKey = `mood_entries_${user.id}`;
      const storedData = localStorage.getItem(storageKey);
      
      if (storedData) {
        console.log('Found localStorage mood data, migrating to Supabase...');
        const localEntries = JSON.parse(storedData);
        
        if (Array.isArray(localEntries) && localEntries.length > 0) {
          // Prepare data for Supabase insertion
          const entriesToInsert = localEntries.map(entry => ({
            user_id: user.id,
            date: entry.date,
            mood: entry.mood,
            created_at: entry.created_at || new Date().toISOString(),
            updated_at: entry.updated_at || new Date().toISOString()
          }));

          // Insert into Supabase
          const { data, error } = await supabase
            .from('mood_tracker')
            .insert(entriesToInsert)
            .select();

          if (error) {
            console.error('Error migrating mood data:', error);
            throw error;
          }

          console.log('Successfully migrated mood data to Supabase:', data);
          
          // Convert and set the migrated data
          const migratedEntries: MoodEntry[] = data.map(row => ({
            id: row.id,
            date: row.date,
            mood: row.mood as MoodType,
            created_at: row.created_at || new Date().toISOString(),
            updated_at: row.updated_at || new Date().toISOString()
          }));
          
          setMoodEntries(migratedEntries);
          
          // Clean up localStorage after successful migration
          localStorage.removeItem(storageKey);
          toast.success(`Migrated ${migratedEntries.length} mood entries to your account`);
        }
      }
    } catch (error) {
      console.error('Error during localStorage migration:', error);
      // Don't show error toast for migration failures, just log them
    }
  };

  const setMoodForDate = async (date: string, mood: MoodType) => {
    console.log('setMoodForDate called:', { date, mood, user: user?.id });
    if (!user) {
      console.log('No user found, returning early');
      return;
    }

    try {
      const now = new Date().toISOString();
      
      // Check if entry already exists for this date
      const existingEntry = moodEntries.find(entry => entry.date === date);
      console.log('Existing entry check:', { date, existingEntry });

      if (existingEntry) {
        console.log('Updating existing mood entry for date:', date);
        
        // Update existing entry in database
        const { error } = await supabase
          .from('mood_tracker')
          .update({ 
            mood,
            updated_at: now
          })
          .eq('id', existingEntry.id)
          .eq('user_id', user.id);

        if (error) {
          console.error('Error updating mood entry:', error);
          throw error;
        }

        // Update local state
        const updatedEntries = moodEntries.map(entry =>
          entry.id === existingEntry.id
            ? { ...entry, mood, updated_at: now }
            : entry
        );
        setMoodEntries(updatedEntries);
        
      } else {
        console.log('Creating new mood entry for date:', date);
        
        // Create new entry in database
        const { data, error } = await supabase
          .from('mood_tracker')
          .insert({
            user_id: user.id,
            date,
            mood,
            created_at: now,
            updated_at: now
          })
          .select()
          .single();

        if (error) {
          console.error('Error creating mood entry:', error);
          throw error;
        }

        console.log('Created new mood entry:', data);

        // Add to local state
        const newEntry: MoodEntry = {
          id: data.id,
          date: data.date,
          mood: data.mood as MoodType,
          created_at: data.created_at || now,
          updated_at: data.updated_at || now
        };
        
        setMoodEntries(prev => [newEntry, ...prev]);
      }

      console.log('Mood successfully saved to database');
      toast.success(`Mood set to ${mood}`);
      
    } catch (error) {
      console.error("Error setting mood:", error);
      toast.error("Failed to save mood");
    }
  };

  const getMoodForDate = (date: string): MoodType | null => {
    const entry = moodEntries.find(entry => entry.date === date);
    return entry ? entry.mood : null;
  };

  const deleteMoodForDate = async (date: string) => {
    if (!user) return;

    try {
      const entryToDelete = moodEntries.find(entry => entry.date === date);
      if (!entryToDelete) return;

      console.log('Deleting mood entry for date:', date);

      // Delete from database
      const { error } = await supabase
        .from('mood_tracker')
        .delete()
        .eq('id', entryToDelete.id)
        .eq('user_id', user.id);

      if (error) {
        console.error('Error deleting mood entry:', error);
        throw error;
      }

      // Update local state
      const updatedEntries = moodEntries.filter(entry => entry.date !== date);
      setMoodEntries(updatedEntries);
      toast.success("Mood removed");
      
    } catch (error) {
      console.error("Error deleting mood:", error);
      toast.error("Failed to delete mood");
    }
  };

  return (
    <MoodTrackerContext.Provider
      value={{
        moodEntries,
        loading,
        setMoodForDate,
        getMoodForDate,
        deleteMoodForDate,
      }}
    >
      {children}
    </MoodTrackerContext.Provider>
  );
} 